import React, { useState } from 'react';
import type { Message } from '../../types/chat.types';
import { CitationCard } from './CitationCard';
import { Icon } from '../common/Icon';
import copySvg from '../../assets/copy.svg';

interface ChatMessageProps {
  message: Message;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === 'user';
  const [copied, setCopied] = useState(false);
  const [isSourcesExpanded, setIsSourcesExpanded] = useState(false);
  const [showAdditional, setShowAdditional] = useState(false);

  // Separate cited sources from additional sources
  const usedCitationNumbers = new Set(
    message.inline_citations?.flatMap(seg => seg.citations) || []
  );

  const citedSources = message.sources?.filter(
    source => usedCitationNumbers.has(source.index)
  ) || [];

  const additionalSources = message.sources?.filter(
    source => !usedCitationNumbers.has(source.index)
  ) || [];

  const copyFullResponse = () => {
    let fullText = message.content;

    if (message.sources && message.sources.length > 0) {
      fullText += '\n\nSources:\n';
      message.sources.forEach(source => {
        fullText += `[${source.index}] ${source.title}\n`;
      });
    }

    navigator.clipboard.writeText(fullText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderAnswerWithCitations = () => {
    if (message.role === 'user') {
      return <p className="text-sm md:text-sm whitespace-pre-wrap break-words leading-relaxed">{message.content}</p>;
    }

    // If no inline citations, render plain text
    if (!message.inline_citations || message.inline_citations.length === 0) {
      return <p className="text-sm md:text-sm whitespace-pre-wrap break-words leading-relaxed">{message.content}</p>;
    }

    return (
      <>
        {/* Render text with inline superscript citations */}
        <div className="text-sm md:text-sm whitespace-pre-wrap break-words leading-relaxed">
          {message.inline_citations.map((segment, idx) => (
            <span key={idx}>
              {segment.text}
              {segment.citations.map(citNum => (
                <sup key={citNum} className="text-blue-600 dark:text-blue-400 cursor-pointer ml-0.5 hover:underline">
                  <a
                    href={`#citation-${message.id}-${citNum}`}
                    onClick={(e) => {
                      e.preventDefault();
                      // Auto-expand sources if collapsed
                      if (!isSourcesExpanded) {
                        setIsSourcesExpanded(true);
                        // Wait for the expansion animation to complete before scrolling
                        setTimeout(() => {
                          const element = document.getElementById(`citation-${message.id}-${citNum}`);
                          element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          element?.classList.add('citation-highlight');
                          setTimeout(() => {
                            element?.classList.remove('citation-highlight');
                          }, 1500);
                        }, 300);
                      } else {
                        const element = document.getElementById(`citation-${message.id}-${citNum}`);
                        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        element?.classList.add('citation-highlight');
                        setTimeout(() => {
                          element?.classList.remove('citation-highlight');
                        }, 1500);
                      }
                    }}
                    aria-label={`Jump to citation ${citNum}`}
                  >
                    [{citNum}]
                  </a>
                </sup>
              ))}
            </span>
          ))}
        </div>

        {/* Copy button when no sources */}
        {!isUser && (!message.sources || message.sources.length === 0) && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={copyFullResponse}
              className="text-gray-400 hover:text-gray-200 transition-colors p-2"
              aria-label="Copy full response"
            >
              {copied ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M13 3L5.5 10.5L3 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <Icon src={copySvg} alt="Copy" size={16} className="brightness-0 invert" />
              )}
            </button>
          </div>
        )}

        {/* Sources and Copy button row */}
        {!isUser && (message.sources && message.sources.length > 0) && (
          <div className="mt-4 space-y-2">
            {/* Header row with Sources button on left and Copy button on right */}
            <div className="flex items-center justify-between gap-2">
              {/* Collapsible Sources button */}
              <button
                onClick={() => setIsSourcesExpanded(!isSourcesExpanded)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 border border-gray-700 transition-all text-sm font-medium text-gray-300"
                aria-label={isSourcesExpanded ? "Collapse sources" : "Expand sources"}
              >
                {/* Sources icon */}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="flex-shrink-0"
                >
                  <path
                    d="M3 3h10v2H3V3zm0 4h10v2H3V7zm0 4h7v2H3v-2z"
                    fill="currentColor"
                    opacity="0.7"
                  />
                </svg>

                {/* Source count */}
                <span>{citedSources.length} {citedSources.length === 1 ? 'source' : 'sources'}</span>
              </button>

              {/* Copy button */}
              <button
                onClick={copyFullResponse}
                className="text-gray-400 hover:text-gray-200 transition-colors p-2"
                aria-label="Copy full response"
              >
                {copied ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M13 3L5.5 10.5L3 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <Icon src={copySvg} alt="Copy" size={16} className="brightness-0 invert" />
                )}
              </button>
            </div>

            {/* Citation cards - conditionally rendered */}
            {isSourcesExpanded && (
              <div className="space-y-3">
                {/* Cited sources */}
                <div className="space-y-2">
                  {citedSources.map((source) => (
                    <div key={source.index} id={`citation-${message.id}-${source.index}`} className="transition-all duration-300">
                      <CitationCard source={source} index={source.index} />
                    </div>
                  ))}
                </div>

                {/* Additional sources toggle and display */}
                {additionalSources.length > 0 && (
                  <div className="space-y-2">
                    {/* Divider */}
                    <div className="border-t border-gray-700/50 pt-3">
                      <button
                        onClick={() => setShowAdditional(!showAdditional)}
                        className="text-xs text-gray-400 hover:text-gray-300 transition-colors flex items-center gap-2"
                        aria-label={showAdditional ? "Hide additional sources" : "Show additional sources"}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          className={`transition-transform duration-200 ${showAdditional ? 'rotate-90' : ''}`}
                        >
                          <path
                            d="M4 2L8 6L4 10"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>
                          {showAdditional
                            ? 'Hide additional sources'
                            : `Show ${additionalSources.length} more related ${additionalSources.length === 1 ? 'source' : 'sources'}`
                          }
                        </span>
                      </button>
                    </div>

                    {/* Additional sources list */}
                    {showAdditional && (
                      <div className="space-y-2 pt-2">
                        <p className="text-xs text-gray-500 italic px-3">
                          Additional Related Sources (not directly cited)
                        </p>
                        {additionalSources.map((source) => (
                          <div key={source.index} id={`citation-${message.id}-${source.index}`} className="transition-all duration-300 opacity-75">
                            <CitationCard source={source} index={source.index} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3 md:mb-4 gap-1.5 md:gap-2`}>
      {/* Bot Avatar (left side for bot messages) */}
      {!isUser && (
        <img
          src="/assets/images/profile_picture.jpg"
          alt="Kapil Gupta"
          className="w-8 h-8 md:w-10 md:h-10 rounded-full border-2 border-primary flex-shrink-0"
        />
      )}

      <div className={`max-w-[95%] md:max-w-[80%]`}>
        <div
          className={`rounded-lg px-2 py-2 md:px-4 md:py-3 ${
            isUser
              ? 'bg-white text-gray-900 border border-gray-300'
              : 'bg-dark-lighter text-white border border-gray-700'
          }`}
        >
          {renderAnswerWithCitations()}
        </div>

        <p className="text-xs text-gray-500 mt-1 px-2">
          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>

      {/* User Avatar (right side for user messages) */}
      {isUser && (
        <img
          src="/assets/images/DinoLogo.svg"
          alt="User"
          className="w-8 h-8 md:w-10 md:h-10 rounded-full border-2 border-gray-300 bg-white p-1 flex-shrink-0"
        />
      )}
    </div>
  );
};
